import React, { useState, useEffect, useRef } from 'react';
import {Link, useLocation} from 'react-router-dom';
import LivePulse from '../components/LivePulse';
import { PRIMARY_COLOR } from '../contants/Colors';
import api from "../api/axios";

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const BUSINESS_TYPES = [
  'Food & Beverage', 'Retail', 'Beauty & Wellness', 'Healthcare',
  'Fitness', 'Education', 'Professional Services', 'Hospitality',
  'Home Services', 'E-commerce', 'Entertainment', 'Technology', 'Other',
];

const LOCATION_OPTIONS = [
  { value: '1 location',    label: '1 location' },
  { value: '2–5 locations',    label: '2–5 locations' },
  { value: '6–15 locations',    label: '6–15 locations' },
  { value: '15+ locations',   label: '15+ locations' },
];

// ── Floating label input ──────────────────────────────────────────────────
function Field({ label, type = 'text', value, onChange, required, as, options, placeholder, hint }) {
  const [focused, setFocused] = useState(false);
  const hasValue = value !== '' && value !== null && value !== undefined;
  const raised = focused || hasValue;

  const base = {
    width: '100%',
    background: '#120f1b',
    border: `1.5px solid ${focused ? 'rgb(255,46,88)' : 'rgba(255,255,255,0.12)'}`,
    borderRadius: 12,
    color: '#fff',
    fontSize: 15,
    outline: 'none',
    transition: 'border-color .2s',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ position: 'relative', marginBottom: 20 }}>
      {/* Floating label */}
      <label style={{
        position: 'absolute',
        left: 16,
        top: raised ? -10 : (as === 'textarea' ? 16 : as === 'select' ? '35%' : '50%'),
        transform: raised ? 'none' : (as === 'textarea' ? 'none' : 'translateY(-50%)'),
        fontSize: raised ? 11 : 14,
        color: raised ? (focused ? 'rgb(255,46,88)' : 'rgba(255,255,255,0.5)') : 'rgba(255,255,255,0.4)',
        fontWeight: raised ? 600 : 400,
        letterSpacing: raised ? '.05em' : 'normal',
        // textTransform: raised ? 'uppercase' : 'none',
        transition: 'all .2s',
        pointerEvents: 'none',
        zIndex: 1,
        // background: raised ? '#120f1b' : 'transparent',
        // background: raised ? 'rgb(8,5,18)' : 'transparent',
        background: '#120f1b',
        padding: raised ? '0 4px' : '0',
        fontFamily: 'Outfit-Light, sans-serif'
      }}>
        {label}{required && <span style={{ color: 'rgb(255,46,88)', marginLeft: 2 }}>*</span>}
      </label>

      {as === 'textarea' ? (
        <textarea
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          required={required}
          rows={4}
          placeholder={focused ? placeholder : ''}
          style={{ ...base, padding: '18px 16px', resize: 'vertical', minHeight: 120 }}
        />
      ) : as === 'select' ? (
          <select
              value={value}
              onChange={onChange}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              required={required}
              style={{
                ...base,
                padding: '18px 16px',
                appearance: 'none',
                cursor: 'pointer',
                maxHeight: '100px',
                overflow: 'hidden',
                overflowY: 'auto',
                color: hasValue ? '#fff' : 'rgba(255,255,255,0.4)',
              }}
          >
            <option value="" disabled hidden>
              {''}
            </option>

            {options.map(o => (
                <option
                    key={o.value || o}
                    value={o.value || o}
                    style={{
                      background: '#1a0828',
                      color: '#fff',
                    }}
                >
                  {o.label || o}
                </option>
            ))}
          </select>
      ) : (
          <input
              type={type}
              value={value}
              onChange={onChange}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              required={required}
              placeholder={focused ? placeholder : ''}
              style={{...base, padding: '18px 16px'}}
          />
      )}

      {hint && <p style={{fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: '4px 0 0 4px', fontFamily: 'Outfit-Light, sans-serif'}}>{hint}</p>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────
export default function Contact() {
  const [scrollY, setScrollY] = useState(0);
  const location = useLocation(); // Hook to listen to route changes

  const [form, setForm] = useState({
    name: '', businessName: '', email: '', phone: '',
    businessType: '', locations: '', message: '',
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef(null);

  // Parse URL query parameter on mount or when location.search changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const locationsParam = searchParams.get('locations');

    // Automatically decodes "%6%E2%80%9315%20locations" into "6–15 locations"
    if (locationsParam) {
      setForm(prev => ({ ...prev, locations: locationsParam }));
    } else {
      setForm(prev => ({ ...prev, locations: '' }));
    }
  }, [location.search]);

  useEffect(() => {
    const fn = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', fn, { passive: true });
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const f = field => ({ value: form[field], onChange: e => setForm({ ...form, [field]: e.target.value }) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    // Inside your submit handler:
    try {
      // Axios automatically stringifies the body, so just pass 'form'
      const response = await api.post('/contact', form);

      // Axios puts the server's response body inside the 'data' property
      const data = response.data;

      setStatus('success');
      setForm({ name:'', businessName:'', email:'', phone:'', businessType:'', locations:'', message:'' });
    } catch (err) {
      setStatus('error');

      // Axios nests the server's error response under err.response
      const errMsg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setErrorMsg(errMsg);
    }
  };

  return (
      <div style={{
        background: '#080512',
        minHeight: '100vh',
        color: '#fff',
        fontFamily: 'Outfit-SemiBold, system-ui, sans-serif',
        overflowX: 'hidden'
      }}>
        <style>{`
        @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:none}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        @keyframes gradShift{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
        .glow-btn:hover{box-shadow:0 0 36px rgba(255,46,88,.5)!important;transform:translateY(-2px)!important;}
        .glow-btn{transition:all .25s!important;}
        input::placeholder,textarea::placeholder{color:rgba(255,255,255,0.25)}
        select option{background:#1a0828;color:#fff}
        .nav-link{color:rgba(255,255,255,.5);text-decoration:none;font-size:14px;transition:color .2s;}
        .nav-link:hover{color:#fff;}
        .nav-links { display: flex; }
        .mobile-signin { display: none; }
        .form-class {
         background: #120f1b;
         border: 1px solid rgba(255,255,255,0.09);
         borderRadius: 20px !important;
         padding: 36px 32px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 16px;
        }
        @media(max-width:768px){
          .nav-links { display: none !important; }
          .mobile-signin { display: block !important; }
          .footer-inner { flex-direction: column !important; text-align: center !important; gap: 12px !important; }
          .nav-inner { padding: 0 20px !important; }
          .form-class {padding: 30px 26px !important; borderRadius: 20px !important;}
          .form-row {
            grid-template-columns: 1fr !important; /* Forces single column on mobile */
          }
        }
        @media(max-width:768px){
          .contact-grid{grid-template-columns:1fr!important;}
          .trust-grid{grid-template-columns:1fr 1fr!important;}
        }
      `}</style>

        {/* ── NAV ── */}
        <nav style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: 80,
          background: scrollY > 60 ? 'rgba(8,5,18,0.92)' : 'transparent',
          backdropFilter: scrollY > 60 ? 'blur(20px)' : 'none',
          borderBottom: scrollY > 60 ? '1px solid rgba(255,255,255,0.06)' : 'none',
          transition: 'all .3s',
        }}>
          <div className="nav-inner" style={{
            maxWidth: 1100,
            margin: '0 auto',
            padding: '0px 40px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: 9}}>
             <Link to={"/"} style={{marginTop: 6}}>
               <img src={'https://pub-267dd4744ace49cf8306de8e7c530c15.r2.dev/assets/Logo-Color.png'}
                    style={{width: 100}} alt="logo"/>
             </Link>
            </div>
            <div className="nav-links" style={{gap: 36, alignItems: 'center'}}>
              <a href="/#features" className="nav-link">Features</a>
              <a href="/#how" className="nav-link">How it works</a>
              {/*<a href="/businesses" className="nav-link">Businesses</a>*/}
              <a href="/#pricing" className="nav-link">Pricing</a>
              <Link to="https://app.theperceptpulse.com/" target={'_blank'} style={{textDecoration: 'none'}}>
                <button style={{
                  background: 'rgba(255,46,88,.1)',
                  border: '1px solid rgba(255,46,88,.3)',
                  color: 'rgb(255,100,130)',
                  padding: '7px 20px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all .2s'
                }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,46,88,.2)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,46,88,.1)'}>
                  Sign in
                </button>
              </Link>
            </div>
            <div className="mobile-signin">
              <Link to="https://app.theperceptpulse.com/" target={'_blank'} style={{textDecoration: 'none'}}>
                <button style={{
                  background: 'rgba(255,46,88,.1)',
                  border: '1px solid rgba(255,46,88,.3)',
                  color: 'rgb(255,100,130)',
                  padding: '6px 14px',
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}>Sign in
                </button>
              </Link>
            </div>
          </div>
        </nav>

        {/* ── HERO ── */}
        <div style={{
          position: 'relative',
          paddingTop: 120,
          paddingBottom: 80,
          paddingLeft: 40,
          paddingRight: 40,
          textAlign: 'center',
          overflow: 'hidden'
        }}>
          {/* Background orbs */}
          <div style={{position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none'}}>
            <div style={{
              position: 'absolute',
              top: '0%',
              left: '20%',
              width: 500,
              height: 500,
              borderRadius: '50%',
              background: 'radial-gradient(circle,rgba(255,46,88,.1),transparent 65%)',
              filter: 'blur(50px)',
              animation: 'float 9s ease-in-out infinite'
            }}/>
            <div style={{
              position: 'absolute',
              top: '10%',
              right: '15%',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: 'radial-gradient(circle,rgba(59,28,98,.2),transparent 65%)',
              filter: 'blur(50px)',
              animation: 'float 11s ease-in-out 1s infinite reverse'
            }}/>
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: 'linear-gradient(rgba(255,255,255,.018) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.018) 1px,transparent 1px)',
              backgroundSize: '72px 72px'
            }}/>
          </div>

          <div style={{
            position: 'relative',
            zIndex: 1,
            maxWidth: 640,
            margin: '0 auto',
            animation: 'fadeUp .9s ease both'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(255,46,88,.1)',
              border: '1px solid rgba(255,46,88,.22)',
              borderRadius: 100,
              padding: '5px 16px',
              marginBottom: 28
            }}>
              <LivePulse label="" color={PRIMARY_COLOR} marginTop={-2}/>
              <span style={{
                fontSize: 11,
                color: 'rgba(255,160,175,.9)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '.08em'
              }}>Let's talk</span>
            </div>

            <h1 style={{
              fontFamily: 'Outfit-Black, sans-serif',
              fontSize: 'clamp(36px,5vw,64px)',
              lineHeight: 1.05,
              letterSpacing: '-2px',
              marginBottom: 20
            }}>
              Ready to hear what your<br/>
              <span style={{
                background: 'linear-gradient(135deg,rgb(255,46,88),rgb(200,20,60),rgb(130,60,180),rgb(59,28,98))',
                backgroundSize: '200% 200%',
                animation: 'gradShift 5s ease infinite',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
              customers really think?
            </span>
            </h1>

            <p style={{fontSize: 17, lineHeight: 1.75, color: 'rgba(255,255,255,.45)', marginBottom: 0, fontFamily: 'Outfit-Light, sans-serif'}}>
              Fill in the form and we'll set you up. Most businesses are live within the same day.
            </p>
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div style={{
          maxWidth: 1060,
          margin: '0 auto',
          padding: '0 40px 100px',
          display: 'grid',
          gridTemplateColumns: '1fr 1.4fr',
          gap: 64,
          alignItems: 'start'
        }} className="contact-grid">

          {/* ── LEFT — info ── */}
          <div style={{paddingTop: 0, animation: 'fadeUp .9s ease .1s both'}}>

            {/* Response time */}
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.09)',
              borderRadius: 16,
              padding: '22px 24px',
              marginBottom: 24
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14}}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(255,46,88,.15)',
                  border: '1px solid rgba(255,46,88,.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 17
                }}>⚡
                </div>
                <div>
                  <div style={{fontWeight: 600, fontSize: 14, color: '#fff'}}>Fast response</div>
                  <div style={{fontSize: 12, color: 'rgba(255,255,255,.4)', fontFamily: 'Outfit-Light, sans-serif'}}>We reply within 24 hours</div>
                </div>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14}}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(59,130,246,.15)',
                  border: '1px solid rgba(59,130,246,.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 17
                }}>🎯
                </div>
                <div>
                  <div style={{fontWeight: 600, fontSize: 14, color: '#fff'}}>Free setup call</div>
                  <div style={{fontSize: 12, color: 'rgba(255,255,255,.4)', fontFamily: 'Outfit-Light, sans-serif'}}>We walk you through everything live</div>
                </div>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(34,197,94,.15)',
                  border: '1px solid rgba(34,197,94,.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 17
                }}>🚀
                </div>
                <div>
                  <div style={{fontWeight: 600, fontSize: 14, color: '#fff'}}>Same-day go live</div>
                  <div style={{fontSize: 12, color: 'rgba(255,255,255,.4)', fontFamily: 'Outfit-Light, sans-serif'}}>Most businesses start collecting feedback
                    today
                  </div>
                </div>
              </div>
            </div>

            {/* What happens next */}
            <div style={{marginBottom: 24}}>
              <p style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(255,255,255,.35)',
                textTransform: 'uppercase',
                letterSpacing: '.1em',
                marginBottom: 14
              }}>What happens next</p>
              {[
                {n: '1', t: 'We review your submission', s: 'Usually within a few hours'},
                {n: '2', t: 'Quick onboarding call', s: '15 minutes — we set up your account together'},
                {n: '3', t: 'You go live', s: 'Staff trained, QR codes printed, collecting feedback'},
              ].map((step, i) => (
                  <div key={i} style={{display: 'flex', gap: 12, marginBottom: 14}}>
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: 'rgba(255,46,88,.15)',
                      border: '1px solid rgba(255,46,88,.3)',
                      color: 'rgb(255,100,130)',
                      fontSize: 11,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      marginTop: 1
                    }}>{step.n}</div>
                    <div>
                      <div style={{fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 2}}>{step.t}</div>
                      <div style={{fontSize: 12, color: 'rgba(255,255,255,.4)', fontFamily: 'Outfit-Light, sans-serif'}}>{step.s}</div>
                    </div>
                  </div>
              ))}
            </div>

            {/* Trust stats */}
            <div className="trust-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12}}>
              {[
                {v: '50+', l: 'Languages supported'},
                {v: '3s', l: 'Avg feedback time'},
                {v: '99%', l: 'Uptime guaranteed'},
                {v: '10×', l: 'More responses vs paper'},
              ].map((s, i) => (
                  <div key={i} style={{
                    background: 'rgba(255,255,255,.03)',
                    border: '1px solid rgba(255,255,255,.07)',
                    borderRadius: 12,
                    padding: '14px 16px',
                    textAlign: 'center',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
                  }}>
                    <div style={{
                      fontFamily: 'Outfit-Black, sans-serif',
                      fontSize: 26,
                      color: 'rgb(255,46,88)',
                      lineHeight: 1,
                      marginBottom: 4
                    }}>{s.v}</div>
                    <div style={{
                      fontSize: 11,
                      color: 'rgba(255,255,255,.38)',
                      textTransform: 'uppercase',
                      letterSpacing: '.07em'
                    }}>{s.l}</div>
                  </div>
              ))}
            </div>

            {/* Direct email */}
            <div style={{
              marginTop: 24,
              padding: '14px 18px',
              background: 'rgba(255,255,255,.03)',
              border: '1px solid rgba(255,255,255,.08)',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}>
              <span style={{fontSize: 18}}>✉️</span>
              <div>
                <div style={{fontSize: 12, color: 'rgba(255,255,255,.4)', fontFamily: 'Outfit-Light, sans-serif'}}>Or email us directly</div>
                <a href="mailto:hello@theperceptpulse.com" style={{
                  fontSize: 14,
                  color: 'rgb(255,100,130)',
                  textDecoration: 'none',
                  fontWeight: 600
                }}>hello@theperceptpulse.com</a>
              </div>
            </div>
          </div>

          {/* ── RIGHT — form ── */}
          <div style={{animation: 'fadeUp .9s ease .2s both'}} ref={formRef}>
            {status === 'success' ? (
                <div style={{
                  background: 'rgba(34,197,94,.07)',
                  border: '1.5px solid rgba(34,197,94,.3)',
                  borderRadius: 20,
                  padding: '56px 40px',
                  textAlign: 'center'
                }}>
                  <div style={{fontSize: 56, marginBottom: 20}}>🎉</div>
                  <h2 style={{
                    fontFamily: 'Outfit-Black, sans-serif',
                    fontSize: 28,
                    letterSpacing: '-1px',
                    marginBottom: 14
                  }}>Message received!</h2>
                  <p style={{fontSize: 16, color: 'rgba(255,255,255,.55)', lineHeight: 1.7, marginBottom: 32, fontFamily: 'Outfit-Light, sans-serif'}}>
                    We'll be in touch within 24 hours. In the meantime, feel free to explore the platform.
                  </p>
                  <div style={{display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap'}}>
                    <Link to="/" style={{textDecoration: 'none'}}>
                      <button style={{
                        background: 'linear-gradient(135deg,rgb(255,46,88),rgb(180,15,52))',
                        border: 'none',
                        color: '#fff',
                        padding: '12px 28px',
                        borderRadius: 10,
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}>Back to home
                      </button>
                    </Link>
                    <button
                        onClick={() => setStatus('idle')}
                        style={{
                          background: 'rgba(255,255,255,.06)',
                          border: '1px solid rgba(255,255,255,.12)',
                          color: 'rgba(255,255,255,.6)',
                          padding: '12px 24px',
                          borderRadius: 10,
                          fontSize: 14,
                          cursor: 'pointer'
                        }}
                    >
                      Send another message
                    </button>
                  </div>
                </div>
            ) : (
                <form
                    onSubmit={handleSubmit}
                    className={'form-class'}
                    style={{borderRadius: 20}}
                >
                  <h2 style={{
                    fontFamily: 'Outfit-Black, sans-serif',
                    fontSize: 22,
                    letterSpacing: '-.5px',
                    marginBottom: 6
                  }}>Get in touch</h2>
                  <p style={{fontSize: 13, color: 'rgba(255,255,255,.4)', marginBottom: 28, fontFamily: 'Outfit-Light, sans-serif'}}>Tell us about your business
                    and we'll tailor the onboarding to you.</p>

                  {status === 'error' && (
                      <div style={{
                        background: 'rgba(239,68,68,.12)',
                        border: '1px solid rgba(239,68,68,.3)',
                        borderRadius: 10,
                        padding: '12px 16px',
                        marginBottom: 20,
                        fontSize: 13,
                        color: '#fca5a5'
                      }}>
                        {errorMsg}
                      </div>
                  )}

                  <div className="form-row">
                    <Field label="Full Name" required {...f('name')} placeholder="Sarah"/>
                    <Field label="Business name" required={true} {...f('businessName')} placeholder="Patel & Pour"/>
                  </div>
                  <div className="form-row">
                    <Field label="Email address" type="email" required {...f('email')} placeholder="you@business.com"/>
                    <Field label="Phone number" required={true} type="tel" {...f('phone')} placeholder="+92 300 1234567"/>
                  </div>
                  <div className="form-row">
                    <Field label="Business type" as="select" required={true} options={BUSINESS_TYPES} {...f('businessType')} />
                    <Field label="How many locations?" as="select" required={true} options={LOCATION_OPTIONS} {...f('locations')}
                           hint="Helps us recommend the right plan"/>
                  </div>
                  <Field
                      label="Message"
                      as="textarea"
                      required
                      {...f('message')}
                      placeholder="Tell us about your business, what you're looking for, any questions…"
                  />

                  <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="glow-btn"
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: status === 'submitting'
                            ? 'rgba(255,46,88,.5)'
                            : 'linear-gradient(135deg,rgb(255,46,88),rgb(180,15,52))',
                        border: 'none',
                        color: '#fff',
                        borderRadius: 12,
                        fontSize: 15,
                        fontFamily: 'Outfit-SemiBold, sans-serif',
                        cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        letterSpacing: '.02em',
                      }}
                  >
                    {status === 'submitting' ? (
                        <>
                          <svg width="16" height="16" viewBox="0 0 16 16"
                               style={{animation: 'spin 1s linear infinite'}}>
                            <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,.4)" strokeWidth="2" fill="none"/>
                            <path d="M8 2 A6 6 0 0 1 14 8" stroke="#fff" strokeWidth="2" fill="none"
                                  strokeLinecap="round"/>
                          </svg>
                          Sending…
                        </>
                    ) : (
                        'Send message'
                    )}
                  </button>

                  <p style={{fontSize: 11, color: 'rgba(255,255,255,.25)', textAlign: 'center', marginTop: 14, fontFamily: 'Outfit-Light, sans-serif'}}>
                    🔒 Your information is private and never shared with third parties.
                  </p>
                </form>
            )}
          </div>
        </div>

        {/* ── FOOTER ── */}
        <footer style={{padding: '32px 40px', borderTop: '1px solid rgba(255,255,255,.06)'}}>
          <div className="footer-inner" style={{
            maxWidth: 1060,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 14
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: 9}}>
              <Link to={'/'}>
                <img src={'https://pub-267dd4744ace49cf8306de8e7c530c15.r2.dev/assets/Logo-Color.png'}
                     style={{width: 100}} alt="logo"/>
              </Link>
            </div>
            <p style={{fontSize: 12, color: 'rgba(255,255,255,.2)'}}>© {new Date().getFullYear()} Percept Pulse. All
              rights reserved.</p>
            <div style={{display: 'flex', gap: 22}}>
              <Link to="https://app.theperceptpulse.com/tenant/login" target={"_blank"} style={{
                fontSize: 12,
                color: 'rgba(255,255,255,.28)',
                textDecoration: 'none',
                transition: 'color .2s'
              }} onMouseOver={e => e.target.style.color = 'rgba(255,255,255,.6)'}
                    onMouseOut={e => e.target.style.color = 'rgba(255,255,255,.28)'}>Tenant login</Link>
              <Link to="https://app.theperceptpulse.com/feedback-taker/login" target={"_blank"} style={{
                fontSize: 12,
                color: 'rgba(255,255,255,.28)',
                textDecoration: 'none',
                transition: 'color .2s'
              }} onMouseOver={e => e.target.style.color = 'rgba(255,255,255,.6)'}
                    onMouseOut={e => e.target.style.color = 'rgba(255,255,255,.28)'}>Staff login</Link>
            </div>
          </div>
        </footer>

        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
  );
}
